<template>
  <nav class="navigation-bar-mobile bb-column">
    <header class="navigation-bar-mobile-header">
      <div
        class="bb-row bb-fcenter-y bb-justify-content-between bb-m-2 bb-pt-2"
      >
        <CloseIcon
          class="close-icon"
          aria-label="Cerrar"
          role="img"
          @click="closeNavBar"
        />
        <router-link :to="{ name: 'home' }">
          <LogoIcon class="bb-logo" aria-label="Bigbox Partners" role="img" />
        </router-link>
      </div>
      <hr class="line" />
    </header>
    <section class="navigation-bar-mobile-profile bb-pt-2">
      <div class="bb-row bb-fcenter-y bb-justify-content-between">
        <router-link
          v-track="HEADER_EVENTS.profile"
          :to="{ name: 'profile' }"
          class="bb-row navigation-bar-mobile-element bb-my-2 bb-ml-6"
        >
          <ProfileIcon
            class="navigation-bar-mobile-icon bb-pr-3"
            aria-label="Perfil"
            role="img"
          />
          <p class="navigation-bar-mobile-text" @click="closeNavBar">
            {{ profileTitle }}
          </p>
        </router-link>
        <qr-code-dropdown
          v-if="qrCodeInfo"
          class="bb-m-2"
          aria-label="Código QR"
          role="img"
          @show-modal="toggleQRCodeModal"
        />
      </div>
      <hr class="line" />
    </section>
    <section class="elements-top bb-column bb-pt-2">
      <template v-for="(item, index) in items" :key="item.name">
        <ul
          v-if="item.kind !== 'help-center'"
          v-track-scope="{ item_index: index + 1 }"
          class="bb-my-3 bb-ml-6 navigation-bar-mobile-element"
          @click="closeNavBar"
        >
          <router-link
            v-track="item.analyticsEvent"
            :to="item.route ?? { name: 'home' }"
            class="bb-row"
            @click="handleNavigationClick(item)"
          >
            <component
              :is="item.icon"
              class="navigation-bar-mobile-icon bb-pr-3"
              :aria-label="item.label"
              role="img"
            />
            <li class="navigation-bar-mobile-text">{{ item.label }}</li>
          </router-link>
        </ul>
        <Accordion
          v-else
          v-track.open="item.analyticsEvent"
          v-track-scope="{ item_index: index + 1 }"
          class="bb-ml-6 bb-mt-3"
        >
          <AccordionTab>
            <template #header>
              <component
                :is="item.icon"
                class="navigation-bar-mobile-icon bb-pr-2"
                :aria-label="item.label"
                role="img"
              />
              <span class="navigation-bar-mobile-text bb-ml-1">
                {{ item.label }}
              </span>
            </template>
            <p class="m-0">
              Si {{ translate('necesitás') }} actualizar
              <strong>tarifas</strong> o {{ translate('querés') }} conocer
              <strong>nuevas propuestas</strong>, contáctate con alguno de
              nuestros ejecutivos:
              <span class="email-text">{{ email }}</span>
            </p>
            <p class="bb-pt-2">
              Para otro tipo de consultas {{ translate('ingresá') }} al
              <a
                class="email-text"
                href="https://ayuda.partners.bigbox.com.ar/lang/es_ar/"
                target="_blank"
              >
                centro de ayuda
              </a>
            </p>
          </AccordionTab>
        </Accordion>
      </template>
    </section>
    <section class="elements-bottom bb-pt-6" @click="closeNavBar">
      <hr class="line" />

      <div
        class="bb-cursor-pointer bb-row navigation-sidebar-element bb-pt-2 bb-pb-2 bb-ml-6"
        @click="logout"
      >
        <LogoutIcon
          class="navigation-bar-mobile-icon bb-pr-3"
          aria-label="Cerrar sesión"
          role="img"
        />
        <p class="navigation-sidebar-text">Cerrar sesión</p>
      </div>
    </section>
  </nav>
</template>
<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useAuth as useVueAuth } from '@websanova/vue-auth/src/v3';
  import { useRouter } from 'vue-router';
  import translate from '@/helpers/translate';

  import ssoAuth from '@/config/sso';
  import { apolloClient } from '@/config/apolloClientConstructor';
  import { usePartnerInfoStore } from '@/stores/partnerInfoStore';
  import { useReservationConfigurationStore } from '@/stores/reservationConfigurationStore';
  import { useNavigation } from '@/navigation/useNavigation';
  import { HEADER_EVENTS } from '@/analytics/events';
  import type { NavItem } from '@/navigation/navigation.config';
  import type { PartnersProfile } from '@/models/ProfileInterface';

  import QrCodeDropdown from '@/components/QrCode/QrCodeDropdown/QrCodeDropdown.vue';
  import Accordion from 'primevue/accordion';
  import AccordionTab from 'primevue/accordiontab';
  import { TLD } from '@/helpers/getLastTld';

  import LogoIcon from '@/assets/icons/LogoPartnersPrimary.svg';
  import CloseIcon from '@/assets/icons/Cross.svg';
  import ProfileIcon from '@/assets/icons/Profile.svg';
  import LogoutIcon from '@/assets/icons/Logout.svg';

  const partnerInfoStore = usePartnerInfoStore();
  const reservationConfigurationStore = useReservationConfigurationStore();
  const { items } = useNavigation();

  const auth = useVueAuth();
  const router = useRouter();

  const emit = defineEmits<{ closeNavBar: [] }>();

  const showQRCodeModal = ref(false);

  const profileTitle = computed(() => {
    if (partnerInfoState.value.user.first_name) {
      return partnerInfoState.value.user.first_name;
    } else {
      return partnerInfoState.value.fantasy_name.length > 19
        ? partnerInfoState.value.fantasy_name?.slice(0, 19) + '...'
        : partnerInfoState.value.fantasy_name;
    }
  });
  const qrCodeInfo = computed(() => {
    /* TODO: It is hidden until changes are made in the back
      return Boolean(partnerInfoStore.profile.qr_code); */
    return false;
  });
  const partnerInfoState = computed<PartnersProfile>(() => {
    return partnerInfoStore.profile;
  });

  const email = computed(() => {
    switch (TLD) {
      case 'ar':
        return 'consultas@bigbox.com.ar';
      case 'uy':
        return 'consultas@bigbox.com.uy';
      case 'mx':
        return 'consultas@bigbox.com.mx';
      case 'pe':
        return 'consultas@bigbox.com.pe';
      case 'cl':
        return 'consultas@bigbox.cl';
      case 'es':
        return 'consultas@bigbox.es';
      default:
        return '';
    }
  });

  function closeNavBar() {
    emit('closeNavBar');
  }

  function handleNavigationClick(item: NavItem) {
    if (item.name === 'reservation-configuration') {
      reservationConfigurationStore.clearLastReservationLocationsViewLocationId();
    }
  }

  function toggleQRCodeModal() {
    showQRCodeModal.value = !showQRCodeModal.value;
  }
  async function logout() {
    await ssoAuth.logout({
      auth,
      apolloClient,
      additionalCleanup: closeNavBar,
    });
    router.push({ name: 'login' });
  }
</script>
<style lang="scss" scoped>
  .navigation-bar-mobile {
    position: fixed;
    top: 0;
    width: 100%;
    height: 100vh;
    background-color: $WHITE;
    z-index: $HEADER-Z-INDEX + 10;
    &-text {
      color: $DARK-GRAY;
      font-weight: 300;
    }
    &-icon {
      width: auto;
      height: 20px;
      color: $DARK-GRAY;
    }

    &-header {
      height: 70px;
    }
    &-profile {
      height: 70px;
    }

    .bb-logo {
      width: 80px;
      height: auto;
      text-align: center;
    }

    .close-icon {
      width: $LARGE;
      margin: -5px;
      height: auto;
      text-align: center;
      color: $DARK-GRAY;
    }
    .line {
      width: 100%;
      border-top: 1px solid #e0e0e0;
      border-bottom: 0;
      border-left: 0;
      border-right: 0;
    }

    .router-link-exact-active {
      color: $PRIMARY;

      svg {
        color: $PRIMARY;
      }
    }

    .elements-top {
      justify-content: flex-start;
      overflow: auto;
      @media (min-width: 540px) and (max-width: 750px) {
        padding-top: 200px;
        overflow: auto;
      }
      @media (width: 768px) {
        justify-content: start;
      }
    }
    .elements-bottom {
      margin-top: auto;
      @media (width: 640px) {
        width: 100%;
        position: fixed;
        bottom: 0px;
        background-color: $WHITE;
      }
    }
    .email-text {
      color: $PRIMARY;
      text-decoration: underline;
    }

    :deep(.p-accordion-header-link) {
      padding: 0 !important;
    }
    :deep(.p-accordion-header-link:hover) {
      background-color: transparent !important;
      padding: 0 !important;
    }
    :deep(.p-accordion-header-link:focus) {
      outline: none !important;
      box-shadow: none !important;
      padding: 0 !important;
    }
  }
</style>
